import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { Helmet } from 'react-helmet';

import { withErrorHandling } from '@selfserve/react-hocs';
import {
  useStorage,
  storage,
  storageKeys,
  useQueryParams,
} from '@selfserve/react-hooks';
import {
  StyledContentContainer,
  StyledTitle,
  StyledText,
  SplitScreenLayout,
  StyledVideoScreen,
  StyledSideImage,
  StyledVideoContainer,
} from '@selfserve/react-ui';
import {
  routesPaths,
  getRecaptchaToken,
  IIndexSignature,
  apiStatuses,
  links,
  gtmEvents,
  responseTypes,
} from '@selfserve/react-utils';

import { GetStartedForm } from '../ui/get-started-form';
import { SocialLogin } from './social-login';
import { useEmailValidationRequest, usePackageInfoRequest } from '../data';

const ALREADY_REGISTERED_ERROR_ID = 10019;
const EMAIL = 'email';

const StyledGetStartedContainer = styled.div(
  ({ theme, isSecondary, hasSocialLogin }: { theme?: any; isSecondary?: boolean, hasSocialLogin?: boolean }) => ({
    maxWidth: '540px',
    width: '100%',
    ...(isSecondary && {
      [theme.breakpoints.up('sm')]: {
        maxWidth: '684px',
        '.form-container': {
          display: 'flex',
          flexDirection: 'row-reverse',
        },
        '.get-started-form': {
          ...(hasSocialLogin ? {
            width: '50%',
          } : {
            width: '100%',
          }),
          paddingRight: '42px',
        },
        '.oauth-block': {
          width: '50%',
          position: 'relative',
          margin: '0',
          padding: '26px 0 0 41px',
          '.divider': {
            display: 'none',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            width: '2px',
            height: '100%',
            background: theme.palette.grey['500'],
            top: 0,
            left: 0,
          },
        },
      },
    }),
  })
);

const StyledGetStartedTitle = styled(StyledTitle)`
  display: block;
  margin-bottom: 25px;
  text-align: center;
`;

const StyledGetStartedText = styled(StyledText)`
  margin-bottom: 48px;
  text-align: center;
`;

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common.validations.email-valid'))
      .required(t('common.validations.email-required')),
  });

const onSubmit =
  (
    handleEmailValidationRequest: (
      payload: IIndexSignature<string | undefined>
    ) => void,
    setIsRecaptchaLoading: (value: boolean) => void,
    setSessionPackageId: (value: string) => void,
    setSessionEmail: (value: string) => void,
    desiredPackageId: string | undefined,
    queryParams: IIndexSignature<string>,
    isLoading: boolean
  ) =>
  (values: FormValues) => {
    if (!isLoading) {
      setIsRecaptchaLoading(true);
      getRecaptchaToken().then((recaptchaToken: string) => {
        const payload = {
          ...values,
          ...queryParams,
          desiredPackageId,
          recaptchaToken,
        };
        handleEmailValidationRequest(payload);
        setIsRecaptchaLoading(false);
        setSessionEmail(values.email);
        setSessionPackageId(desiredPackageId as string);
      });
    }
  };

export interface FormValues {
  email: string;
}

interface Props {
  isSecondary?: boolean;
}

export const GetStarted = withErrorHandling<Props>(
  ({ openErrorModal, isSecondary }) => {
    const navigate = useNavigate();
    const { desiredPackageId } = useParams();
    const queryParams = useQueryParams();
    const { t } = useTranslation();
    const [isSocialLoading, setIsSocialLoading] = useState(false);
    const [validateOnBlur, setValidateOnBlur] = useState(false);
    const [validateOnChange, setValidateOnChange] = useState(false);
    const [isRecaptchaLoading, setIsRecaptchaLoading] = useState(false);
    const [currentExistingUser, setExistingUser] = useStorage(
      storage.LOCAL,
      storageKeys.EXISTING_USER,
      false
    );
    const [, setSessionEmail] = useStorage(
      storage.SESSION,
      storageKeys.EMAIL,
      ''
    );
    const [, setSessionPackageId] = useStorage(
      storage.SESSION,
      storageKeys.PACKAGE_ID,
      desiredPackageId
    );
    const {
      data: emailValidationResponse,
      isLoading: isRequestLoading,
      mutateAsync: handleEmailValidationRequest,
      isSuccess,
    } = useEmailValidationRequest();
    const { data: packageInfoResponse } = usePackageInfoRequest(
      desiredPackageId as string
    );
    const isLoading = isRequestLoading || isRecaptchaLoading || isSocialLoading;
    const formik: FormikProps<FormValues> = useFormik<FormValues>({
      validateOnBlur,
      validateOnChange,
      initialValues: { email: '' },
      validationSchema: validationSchema(t),
      onSubmit: onSubmit(
        handleEmailValidationRequest,
        setIsRecaptchaLoading,
        setSessionPackageId,
        setSessionEmail,
        desiredPackageId,
        queryParams,
        isLoading
      ),
    });
    const { errors, values, setFieldError } = formik;
    const { getStartedText } = packageInfoResponse?.data || {};
    const defaultMediaLink = process.env['NX_EMAIL_VIDEO'];

    useEffect(() => {
      if (formik.dirty && !validateOnBlur) setValidateOnBlur(true);
    }, [formik.dirty, validateOnBlur]);

    useEffect(() => {
      if (errors.email && !validateOnChange) {
        setValidateOnChange(true);
      } else if (!errors.email) {
        setValidateOnChange(false);
      }
    }, [validateOnChange, errors]);

    useEffect(() => {
      if (isSuccess) {
        const {status, data} = emailValidationResponse || {};
        if (String(status).startsWith(responseTypes.SUCCESS)) {
          const {url, existingUser} = data || {};
          if (existingUser) {
            setExistingUser(existingUser);
          } else if (currentExistingUser) {
            setExistingUser(false);
          }
          if (url) {
            window.location.href = url;
          } else {
            navigate(routesPaths.VERIFY);
          }
          (window as any).dataLayer.push({
            event: gtmEvents.EMAIL_SUBMITION,
            email: values.email,
          });
        } else {
          const {message_en} = data || {};
          setFieldError(EMAIL, message_en);
          if (data?.id === ALREADY_REGISTERED_ERROR_ID) {
            openErrorModal({
              url: routesPaths.LOGIN,
              text: t(`errors.${ALREADY_REGISTERED_ERROR_ID}`),
              confirmBtnText: t('common.login'),
              preventActionsOnClose: true,
              withDissmiss: true,
            });
          } else if (data?.statusCode === apiStatuses.BAD_REQUEST) {
            setFieldError(EMAIL, data?.message?.join('\n'));
          }
        }
      }
    }, [emailValidationResponse, isSuccess, navigate, setFieldError]);

    useEffect(() => {
      if (
        packageInfoResponse?.status === apiStatuses.SERVER_ERROR ||
        packageInfoResponse?.status === apiStatuses.NOT_FOUND
      ) {
        openErrorModal({
          url: links.PRICING,
          text: t(`errors.${packageInfoResponse.data?.id}`),
        });
      }
    }, [emailValidationResponse, packageInfoResponse, openErrorModal]);

    const SecondScreen = useMemo(() => {
      if (defaultMediaLink || !packageInfoResponse) {
        const mediaLink = packageInfoResponse?.data?.gettingStartedMediaUrl || defaultMediaLink;

        return (
          <StyledVideoContainer>
            { packageInfoResponse?.status === apiStatuses.SUCCESS &&
              <StyledVideoScreen autoPlay loop muted poster={mediaLink}>
                <source src={mediaLink} type="video/mp4" />
              </StyledVideoScreen>
            }
          </StyledVideoContainer>
        );
      }

      return <StyledSideImage className="meetings-icon" />;
    }, [defaultMediaLink, packageInfoResponse]);

    return (
      <>
        <Helmet>
          <title>{t('titles.create-account')}</title>
        </Helmet>
        <SplitScreenLayout secondScreen={SecondScreen}>
          <StyledContentContainer centered>
            <StyledGetStartedContainer isSecondary={isSecondary} hasSocialLogin={packageInfoResponse?.data?.enableSocialLoginFlow}>
              <StyledGetStartedTitle as="span" className="main-header">
                {t('get-started.lets-get-started')}
              </StyledGetStartedTitle>
              {getStartedText && (
                <StyledGetStartedText className="getting-started-description">
                  {getStartedText}
                </StyledGetStartedText>
              )}
              <div className="form-container">
                {
                  packageInfoResponse?.data?.enableSocialLoginFlow && (
                    <SocialLogin
                      onLoadingChange={setIsSocialLoading}
                      openErrorModal={openErrorModal}
                      setFieldError={setFieldError}
                      loading={isLoading}
                    />
                  )
                }
                <GetStartedForm formik={formik} isLoading={isLoading} />
              </div>
            </StyledGetStartedContainer>
          </StyledContentContainer>
        </SplitScreenLayout>
      </>
    );
  }
);
